'use client'

import { createContext, type ReactNode, useContext } from 'react'
import { useAppLocale } from '@/components/i18n/locale-provider'
import { localizePath } from '@/lib/i18n/routing'
import {
  flowsBasePathForSurface,
  templatesBasePathForSurface,
} from './url-helpers'

export type TemplatesSurface = 'app' | 'public'

const TemplatesSurfaceContext = createContext<TemplatesSurface>('app')

export function TemplatesSurfaceProvider({
  surface,
  children,
}: {
  surface: TemplatesSurface
  children: ReactNode
}) {
  return (
    <TemplatesSurfaceContext.Provider value={surface}>
      {children}
    </TemplatesSurfaceContext.Provider>
  )
}

export function useTemplatesSurface(): TemplatesSurface {
  return useContext(TemplatesSurfaceContext)
}

/**
 * `/templates` inside the app; `/browse/templates` on the public mirror, in
 * the reader's language (`/de/browse/templates`).
 */
export function useTemplatesBasePath(): string {
  const locale = useAppLocale()
  return localizePath(
    locale,
    templatesBasePathForSurface(useTemplatesSurface())
  )
}

/** `/flows` inside the app, `/browse/flows` (localized) on the public mirror. */
export function useFlowsBasePath(): string {
  const locale = useAppLocale()
  return localizePath(locale, flowsBasePathForSurface(useTemplatesSurface()))
}
